import { createSlice } from "@reduxjs/toolkit";
import {
  adminAuthTokenCookieKey,
  adminProfileCookieKey,
  clientAuthTokenCookieKey,
  clientProfileCookieKey,
  salesAuthTokenCookieKey,
  salesProfileCookieKey,
} from "../../../utils/constants";
import { removeCookie, setCookie } from "../../../utils/helpers";
import { AddressDetails } from "../../../utils/types";
import { PayoutCurrency } from "../payment/payment.types";
import { AdminAuthProfile, AuthProfile, SalesAuthProfile } from "./auth.types";

const initialState: {
  profile: AuthProfile | null;
  isSignedIn: boolean;
  hasCreatedEvent: boolean;
  hasCompletedProfile: boolean;
  hasPayoutAccount: Record<PayoutCurrency, boolean>;
  adminProfile: AdminAuthProfile | null;
  salesProfile: SalesAuthProfile | null;
  isAdminSignedIn: boolean;
  isSalesSignedIn: boolean;
  deviceCoordinates?: { lng: number; lat: number };
  deviceAddressDetails?: AddressDetails;
  backUpCodes: string[];
} = {
  profile: null,
  isSignedIn: false,
  hasCreatedEvent: false,
  hasCompletedProfile: false,
  hasPayoutAccount: {
    "Canadian Dollars": false,
    NGN: false,
    // "US Dollars": false,
  },
  adminProfile: null,
  salesProfile: null,
  isAdminSignedIn: false,
  isSalesSignedIn: false,
  deviceCoordinates: undefined,
  deviceAddressDetails: undefined,
  backUpCodes: [],
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    signUserIn: (
      state,
      action: {
        payload: { profile: AuthProfile; token: string };
        type: string;
      },
    ) => {
      setCookie(clientAuthTokenCookieKey, action.payload.token);
      setCookie(clientProfileCookieKey, JSON.stringify(action.payload.profile));
      state.profile = action.payload.profile;
      state.isSignedIn = true;
      state.hasPayoutAccount = initialState.hasPayoutAccount;
    },
    signAdminIn: (
      state,
      action: {
        payload: { profile: AdminAuthProfile; token: string };
        type: string;
      },
    ) => {
      setCookie(adminAuthTokenCookieKey, action.payload.token);
      setCookie(adminProfileCookieKey, JSON.stringify(action.payload.profile));
      state.adminProfile = action.payload.profile;
      state.isAdminSignedIn = true;
    },
    signSalesIn: (
      state,
      action: {
        payload: { profile: SalesAuthProfile; token: string };
        type: string;
      },
    ) => {
      setCookie(salesAuthTokenCookieKey, action.payload.token);
      setCookie(salesProfileCookieKey, JSON.stringify(action.payload.profile));
      state.salesProfile = action.payload.profile;
      state.isSalesSignedIn = true;
    },
    signUserOut: (state) => {
      removeCookie(clientAuthTokenCookieKey);
      removeCookie(clientProfileCookieKey);
      localStorage.removeItem("persist:root");
      state.profile = null;
      state.isSignedIn = false;
      state.hasCreatedEvent = false;
      state.hasCompletedProfile = false;
      state.hasPayoutAccount = initialState.hasPayoutAccount;
    },
    signAdminOut: (state) => {
      removeCookie(adminAuthTokenCookieKey);
      removeCookie(adminProfileCookieKey);
      localStorage.removeItem("persist:root");
      state.adminProfile = null;
      state.isAdminSignedIn = false;
    },
    signSalesOut: (state) => {
      removeCookie(salesAuthTokenCookieKey);
      removeCookie(salesProfileCookieKey);
      localStorage.removeItem("persist:root");
      state.salesProfile = null;
      state.isSalesSignedIn = false;
    },
    updateProfile: (
      state,
      action: {
        payload: { profile: AuthProfile };
        type: string;
      },
    ) => {
      setCookie(clientProfileCookieKey, JSON.stringify(action.payload.profile));
      state.profile = action.payload.profile;
    },
    updateAdminProfile: (
      state,
      action: {
        payload: { profile: AdminAuthProfile };
        type: string;
      },
    ) => {
      setCookie(adminProfileCookieKey, JSON.stringify(action.payload.profile));
      state.adminProfile = action.payload.profile;
    },
    updateSalesProfile: (
      state,
      action: {
        payload: { profile: SalesAuthProfile };
        type: string;
      },
    ) => {
      setCookie(salesProfileCookieKey, JSON.stringify(action.payload.profile));
      state.salesProfile = action.payload.profile;
    },
    markCreateEvent: (state) => {
      state.hasCreatedEvent = true;
    },
    unMarkCreateEvent: (state) => {
      state.hasCreatedEvent = false;
    },
    markHasCompletedProfile: (
      state,
      action: {
        payload: boolean;
        type: string;
      },
    ) => {
      state.hasCompletedProfile = action.payload;
    },
    markHasPayoutAccount: (
      state,
      action: {
        payload: Record<PayoutCurrency, boolean>;
        type: string;
      },
    ) => {
      state.hasPayoutAccount = action.payload;
    },
    updateDeviceCoordinates: (
      state,
      action: {
        payload: { lng: number; lat: number };
        type: string;
      },
    ) => {
      state.deviceCoordinates = action.payload;
    },
    updateDeviceAddressDetails: (
      state,
      action: {
        payload: AddressDetails;
        type: string;
      },
    ) => {
      state.deviceAddressDetails = action.payload;
    },
    setAdminBackUpCodes: (
      state,
      action: {
        payload: {
          profile: AdminAuthProfile;
          token: string;
          backupCodes: string[];
        };
        type: string;
      },
    ) => {
      setCookie(adminAuthTokenCookieKey, action.payload.token);
      setCookie(adminProfileCookieKey, JSON.stringify(action.payload.profile));
      state.adminProfile = action.payload.profile;
      state.isAdminSignedIn = true;
      state.backUpCodes = action.payload.backupCodes;
    },
    updateAdminBackUpCodes: (
      state,
      action: {
        payload: { backupCodes: string[] };
        type: string;
      },
    ) => {
      state.backUpCodes = action.payload.backupCodes;
    },
  },
});

export const authSliceReducer = authSlice.reducer;
export const authSliceActions = authSlice.actions;
export const authSliceReducerpath = authSlice.reducerPath;
